import PropTypes from 'prop-types';
import React, { Component } from 'react';
import styled from 'styled-components';
import { datadogRum } from '@datadog/browser-rum';
import CashFlowChart from '../CashFlowChart/CashFlowChartContainer';
import Capsule, { PAD_FOR_LEGEND } from '../CommonComponents/Capsule';
import LegacyExecutiveSummary from '../ExecutiveSummary/ExecutiveSummaryContainer';
import FinancialPlanCapsuleWrapper from '../FinancialPlanCapsuleWrapper/FinancialPlanCapsuleWrapperContainer';
import GoalCarousel from '../Goals/GoalCarouselContainer';
import { InlineContainer } from '../Profile/ProfileStyles';
import { EmptyStatesContainer, Heading1, Heading2 } from './ConsumerOverviewStyles';
import EmptyState from './EmptyStateView';
import UpsertAccountModal from '../UpsertAccountModal/UpsertAccountModalContainer';
import { PlaidLinkInfoAlert } from '../Plaid/components/PlaidLinkInfoAlert/PlaidLinkInfoAlert';
import { OverviewNetworthChart } from './OverviewNetworthChart';

const hasEmptyStateLine = (
  hasPersonalFinances,
  canAccessCurrentFinances,
  hasFinancialPlanning,
  canAccessFinancialPlan,
) => {
  const secondColumn = !hasPersonalFinances && canAccessCurrentFinances;
  const thirdColumn = !hasFinancialPlanning && canAccessFinancialPlan;
  return secondColumn || thirdColumn;
};

const Wrapper = styled.div`
  display: flex;
  align-items: flex-start;

  > * {
    width: 100% !important;

    &:first-child {
      width: 60%;
      flex-grow: 1;
      margin-right: 16px;
    }

    &:last-child {
      width: 370px;
      flex-grow: 0;
      flex-shrink: 0;
      display: block !important;

      > * + * {
        margin-top: 24px;
      }
    }
  }
`;

class ConsumerOverviewView extends Component {
  componentDidMount() {
    const {
      fetchSummary,
      currentClient,
      fetchNetWorthData,
      hasDataAggregation,
      portalName,
    } = this.props;

    const { clientId } = datadogRum.getUser();

    if (clientId !== currentClient.Id) {
      datadogRum.setUserProperty('clientId', currentClient.Id);
    }

    if (!hasDataAggregation) {
      fetchNetWorthData(currentClient.Id);
    }
    fetchSummary(currentClient.Id);
  }

  render() {
    const {
      clientId,
      isExecutiveSummaryInitialized,
      hasPersonalFinances,
      hasFinancialPlanning,
      canAccessCurrentFinances,
      canAccessFinancialPlan,
      hasCashflow,
      hasAccessToLinkAccounts,
      openAddAccountModal,
      showNetWorth,
    } = this.props;

    return (
      <div id="ConsumerOverview">
        <InlineContainer>
          <div>
            <Heading1>Overview</Heading1>
            <Heading2>Let’s manage your new financial journey.</Heading2>
          </div>
        </InlineContainer>

        <PlaidLinkInfoAlert mb="16" />

        <Wrapper>
          <section>
            <div className="tile full-width">
              {isExecutiveSummaryInitialized && <LegacyExecutiveSummary />}
            </div>

            {isExecutiveSummaryInitialized && (
              <>
                {hasEmptyStateLine(
                  hasPersonalFinances,
                  canAccessCurrentFinances,
                  hasFinancialPlanning,
                  canAccessFinancialPlan,
                ) && (
                  <div className="tile full-width">
                    <EmptyStatesContainer>
                      {!hasPersonalFinances && canAccessCurrentFinances && (
                        <>
                          <EmptyState
                            title="Personal Finances"
                            subtitle="Understand where your money is going and how your investments are performing."
                            buttonText="Add Financial Accounts"
                            image={`${ADV.settings.endpoints.imgDir}Userportal-01.png`}
                            onButtonClick={() => {
                              if (hasAccessToLinkAccounts) {
                                ADV.util.Router.go2('FinancialAccountsWorkflow', { clientId });
                              } else {
                                openAddAccountModal();
                              }
                            }}
                          />
                          <UpsertAccountModal />
                        </>
                      )}

                      {!hasFinancialPlanning && canAccessFinancialPlan && (
                        <EmptyState
                          title="Financial Planning"
                          subtitle="The best way to reach your financial goals is to have a plan. Even if the future is uncertain, set a goal today."
                          buttonText="Start Planning"
                          image={`${ADV.settings.endpoints.imgDir}Userportal-02.png`}
                          onButtonClick={() => {
                            ADV.util.Router.go2('ClientEditPlan', { clientId });
                          }}
                        />
                      )}
                    </EmptyStatesContainer>
                  </div>
                )}

                {hasFinancialPlanning && canAccessFinancialPlan && (
                  <div className="tile full-width">
                    <FinancialPlanCapsuleWrapper capsuleId="FinancialPlan" clientId={clientId}>
                      <GoalCarousel key={clientId} clientId={clientId} />
                    </FinancialPlanCapsuleWrapper>
                  </div>
                )}

                {hasPersonalFinances && canAccessCurrentFinances && showNetWorth && (
                  <div className="tile full-width">
                    <Capsule
                      header="Net Worth"
                      subheader={<span>(Last 30 Days)</span>}
                      href={canAccessCurrentFinances && 'NetWorth'}
                      className={PAD_FOR_LEGEND}
                      params={{ clientId, pivot: 0 }}
                    >
                      <OverviewNetworthChart clientId={clientId} range="Last 30 Days" />
                    </Capsule>
                  </div>
                )}

                {hasCashflow &&
                  hasPersonalFinances &&
                  canAccessCurrentFinances &&
                  hasAccessToLinkAccounts && (
                    <div key={0} className="tile full-width">
                      <EmptyStatesContainer>
                        <Capsule
                          header="Transactions"
                          subheader={<span>Current (past 30 days)</span>}
                          href={canAccessCurrentFinances && 'Transactions'}
                          params={{ clientId }}
                          className="transactions-capsule"
                        >
                          <CashFlowChart clientId={clientId} />
                        </Capsule>
                      </EmptyStatesContainer>
                    </div>
                  )}
              </>
            )}
          </section>
        </Wrapper>
      </div>
    );
  }
}

ConsumerOverviewView.propTypes = {
  clientId: PropTypes.string.isRequired,
  hasDataAggregation: PropTypes.bool.isRequired,
  hasAccessToLinkAccounts: PropTypes.bool.isRequired,
  isExecutiveSummaryInitialized: PropTypes.bool.isRequired,
  hasPersonalFinances: PropTypes.bool.isRequired,
  hasFinancialPlanning: PropTypes.bool.isRequired,
  canAccessCurrentFinances: PropTypes.bool.isRequired,
  canAccessFinancialPlan: PropTypes.bool.isRequired,
  hasCashflow: PropTypes.bool.isRequired,
  openAddAccountModal: PropTypes.func.isRequired,
  fetchNetWorthData: PropTypes.func.isRequired,
  fetchSummary: PropTypes.func.isRequired,
  currentClient: PropTypes.shape({
    Id: PropTypes.string,
    ShowNewsFeed: PropTypes.bool,
  }).isRequired,
  showNetWorth: PropTypes.bool.isRequired,
  portalName: PropTypes.string.isRequired,
};

export default ConsumerOverviewView;
